/* Copyright 2012-2019 The MathWorks, Inc. */

/**
 * This is a widget which opens up as a result of the click action of the gallery drop down.
 * It has a search bar for filtering category items.
 * It supports two views: LIST and ICON
 */

define([
    "dojo/_base/declare",
    "dojo/on",
    "dijit/popup",
    "./GalleryPanel",
    "MW/toolstrip/constants/gallery/GalleryViewConstants"
], function (declare, on, popup, GalleryPanel, GalleryViewConstants) {

    // TODO: Move this to domUtils
    var matchesSelector = function (node, selector) {
        var matchesMethod;

        if (!node || !selector || !(node instanceof Element)) {
            return false;
        }

        if (node.msMatchesSelector) {
            matchesMethod = "msMatchesSelector";
        } else {
            matchesMethod = "matches";
        }

        return node[matchesMethod](selector);
    };

    return declare([GalleryPanel], {

        /** @lends GalleryPopupWidget# */
        /**
         * Represents a Gallery Popup Widget. This is a widget which opens up as a result of
         * the click action of the gallery drop down. It has a search bar for filtering categoryl
         * items. It supports two views: LIST and ICON
         */

        /**
         * @override
         */
        postCreate: function () {
            this.inherited(arguments);

            this._setFocusType();
            this.domNode.classList.add("galleryPopup");

            this._handleCloseButtonClick = function () {
                popup.close(this);
            }.bind(this);

            this.closeButtonNode.setAttribute("tabindex", "0");

            // TODO: Remove both the below focus and blur listeners and use ":focus-within" CSS selector when Edge
            // and IE start supporting it.
            this.own(on(this.closeButtonNode, "focus", this._handleCloseButtonFocus.bind(this)));
            this.own(on(this.closeButtonNode, "blur", this._handleCloseButtonBlur.bind(this)));

            on(this.closeButtonNode,"click", this._handleCloseButtonClick);
            this._handleFocusChange = function (e) {
                // g1727801: Ideally GalleryPopup would not have knowledge of these exceptions.
                if ((matchesSelector(e.target, ".mwToolstripPopupContextMenu, .mwToolstripPopupContextMenu *")) ||
                    (e.target.hasAttribute("dijitpopupparent") &&
                    matchesSelector(e.target.firstChild, ".mwToolstripPopupContextMenu, .mwToolstripPopupContextMenu *"))) {
                    return;
                }

                if (e.target && (e.target instanceof Element) &&
                    this.domNode &&
                    this.domNode.parentElement &&
                    (this.domNode.parentElement.style.display !== "none") &&
                    (!this.domNode.contains(e.target)) &&
                    (this.domNode !== e.target)) {

                    // Needed to prevent gallery popups from drop down buttons closing and reopening on click of button
                    if (this._invokingWidget) {

                        if (this._invokingWidget.domNode && !this._invokingWidget.domNode.contains(e.target)) {

                            popup.close(this);

                        }

                    } else {
                        popup.close(this);
                    }
                }

                this._handleWindowBlur(e);
            }.bind(this);

            this._handleWindowBlur = function (e) {
                if ((e.target === window) ||
                    (e.target === document.documentElement)) {
                    popup.close(this);
                }
            }.bind(this);

            this.domNode.style.display = "none";
            document.body.appendChild(this.domNode);
        },

        _handleCloseButtonFocus: function () {
            if (this.closeButtonWrapper) {
                this.closeButtonWrapper.classList.add("mwCloseButtonFocused");
            }
        },

        _handleCloseButtonBlur: function () {
            if (this.closeButtonWrapper) {
                this.closeButtonWrapper.classList.remove("mwCloseButtonFocused");
            }
        },

        _setFocusType: function () {
            this.domNode.setAttribute("data-focus", "temporary");
        },

        _calculateSelfMaxHeight: function () {
            var maxHeight = window.innerHeight - this.domNode.getBoundingClientRect().top;
            this.domNode.style.maxHeight = maxHeight + "px";
        },

        _calculateSelfMaxWidth: function () {
            var galleryPopupXCoordinate = this.domNode.getBoundingClientRect().x || 0; // the value is coming undefined on edge
            var maxWidth = window.innerWidth - galleryPopupXCoordinate;
            this.domNode.style.maxWidth = maxWidth + "px";
        },

        _resizeContainerNode: function (columnCount) {
            // Magic number "8" is margin-left + margin-right of category containerNode
            // + border-left + border-right of domNode
            // Number "20" is to account for the scroll bar width
            var galleryPopupWidth = (columnCount * this.galleryItemWidth) + 8 + 20;
            this.domNode.style.width = galleryPopupWidth + "px";
        },

        _setColumnMajorLayoutAttr: function (value) {
            if (this.get("columnMajorLayout") !== value) {
                throw new Error("'columnMajorLayout' property can only be set one time during widget creation using" +
                    " a config file or uiBuilder API to create Gallery Popup. It's value cannot be changed later.");
            }
        },

        _setColumnCountAttr: function (value) {
            if (typeof value !== "number" || value < 0) {
                throw new Error("'columnCount' property expects a positive integer number!");
            }

            this._set("columnCount", value);
        },

        beforeOpen : function () {
            var columnCount;

            // since gallery is controlling it's popup width
            if (!this._invokingGallery) {
                columnCount = this.get("columnCount") || 7;
                this._resizeContainerNode(columnCount);
            }
        },

        updateColumnMajorLayout: function () {
            // Only apply column major layout if GalleryPopup is started and the display state is icon view. It is
            // important that GalleryPopup is started because we are supporting column major layout for widgets other
            // than Gallery that open GalleryPopup. The only way to distinguish whether the GalleryPopup is opened
            // through Gallery or any other widget is to look at _invokingWidget. If it is undefined then we would
            // know that the GalleryPopup was not opened through Gallery. However, during widget creation (before
            // widget is started), _invokingWidget will be undefined even for GalleryPopup associated with a
            // Gallery. Thus, _started check helps to ignore such GalleryPopups associated with a Gallery.
            if (this._started && this.displayState === GalleryViewConstants.ICON) {
                // This class adds display: grid and grid-auto-flow: column styles on the DOM node.
                this.domNode.classList.add("columnMajorLayout");

                var categories = this.getChildren(), columnCount;
                if (categories && categories.length > 0) {
                    columnCount = this.get("columnCount"); // Get the column count from the GalleryPopup

                    if (columnCount !== 0) {
                        categories.forEach(function (category) {
                            this._addColumnMajorLayoutStylesToCategory(category, columnCount);
                        }.bind(this));

                        if (this._favCategoryWidget) {
                            this._addColumnMajorLayoutStylesToCategory(this._favCategoryWidget, columnCount);
                        }
                    }
                }
            }
        },

        _addColumnMajorLayoutStylesToCategory: function (category, columnCount) {
            var visibleChildrenCount = this._getNumberOfVisibleCategoryChildren(category);

            if (visibleChildrenCount > 0) {
                this._setGridTemplateRowsAndColumnsOnCategory(category, visibleChildrenCount, columnCount);
            }
        },

        _getNumberOfVisibleCategoryChildren: function (category) {
            var categoryChildren = category.getChildren(), visibleChildrenCount = 0;
            categoryChildren.forEach(function (child) {
                if (child.domNode && !child.domNode.classList.contains("hideFromView")) {
                    visibleChildrenCount = visibleChildrenCount + 1;
                }
            });

            return visibleChildrenCount;
        },

        _setGridTemplateRowsAndColumnsOnCategory: function (category, childCount, columnCount) {
            var rowCount = Math.ceil(childCount / columnCount);
            category.containerNode.style.gridTemplateColumns = "repeat(" + columnCount + ", 1fr)";
            category.containerNode.style.gridTemplateRows = "repeat(" + rowCount + ", 1fr)";
        },

        /**
         * @override from GalleryPanel
         */
        _isInPopup: function () {
            return true;
        },

        refreshGallery: function () {
            if (this._invokingGallery && this._invokingGallery.refresh) {
                this._invokingGallery.refresh();
            }
        },

        onOpen: function () {
            this.inherited(arguments);
        
            this.isShowingNow = true;
            this.domNode.parentNode.classList.add("galleryPopupWrapper");
            // overriding popup.js height calculation
            this.domNode.parentNode.style.height = "auto";
            this._calculateSelfMaxWidth();
            this._calculateSelfMaxHeight();
            this._checkForPopupListInFooter();
            this._toggleDisplay(this.noSearchMatchNode, false); // Do not show unless a search occurs

            // Wait for the gallery to open, and then focus the search box
            setTimeout(function () {
                this.focus();
                // Add listeners after initial blur/focus events fire from opening Gallery Popup
                setTimeout(function () {
                        // Handle any events that show the user's focus has moved outside of the popup.
                        document.addEventListener("mousedown", this._handleFocusChange,true);
                        document.addEventListener("pointerdown", this._handleFocusChange,true);
                        window.addEventListener("blur", this._handleWindowBlur);
                        this.globalListenersCreated = true;
                    }.bind(this), 0);
            }.bind(this), 10);

            if (!this._invokingGallery && this.get("columnMajorLayout")) {
                // Update the column major layout (currently only supported for widgets other than gallery that open
                // GalleryPopup)
                this.updateColumnMajorLayout();
            }
            this.emit("open");
        },

        onClose: function () {
            this.inherited(arguments);
            this.isShowingNow = false;
            this._resetSearchBoxValue();

            document.removeEventListener("mousedown", this._handleFocusChange);
            document.removeEventListener("pointerdown", this._handleFocusChange);
            window.removeEventListener("blur", this._handleWindowBlur);
            delete this.globalListenersCreated;

            this.emit('restorefocus');
        },

        destroy: function () {
            this.inherited(arguments);
             // If the widget was destroyed before being closed, then we need to clean up
            if (this.globalListenersCreated) {
                window.removeEventListener("blur", this._handleWindowBlur);
            }
        },

        //Clicking on Radio button closes popup. So overriding onExecute method
        onExecute: function () {},

        _qeCloseClick: function () {
            this._handleCloseButtonClick();
        }
    });
});